import {
  index,
  get,
  post,
  put,
  remove,
  restore,
  postOnboarding,
  removeOnboarding,
} from "src/api/file";
import FileForm from "src/modules/purchase-order/components/forms/FileForm.vue";
import { useDealSettingStore } from "./deal-setting";

const getInitialState = () => {
  return {
    files: [],
    file: {},
  };
};

const prepareFile = (file) => {
  const {
    po_id,
    bill_id,
    cn_id,
    dn_id,
    ewaybill_id,
    invc_id,
    pod_id,
    ...rest
  } = file;

  file = {
    ...rest,
    po_id: po_id?._id || po_id,
    bill_id: bill_id?._id || bill_id,
    cn_id: cn_id?._id || cn_id,
    dn_id: dn_id?._id || dn_id,
    ewaybill_id: ewaybill_id?._id || ewaybill_id,
    invc_id: invc_id?._id || invc_id,
    pod_id: pod_id?._id || pod_id,
  };

  return file;
};

async function removeOnboardingFile(_id, user_document) {
  try {
    const payload = {
      user_document_id: user_document._id,
    };

    const response = await removeOnboarding(_id, payload);

    return response;
  } catch (error) {
    console.error(error);

    throw error;
  }
}

async function restoreFile(_id, user_id) {
  const payload = {
    user_id,
  };

  const response = await restore(_id, payload);

  return response;
}

export const useFileStore = defineStore("file", () => {
  const formState = ref({
    edit: false,
    open: false,
    props: null,
  });

  const toggleFileForm = (state = {}) => {
    formState.value = { edit: false, open: !unref(formState).open, ...state };

    const { toggleRightDrawer } = useAppStore();

    toggleRightDrawer(unref(formState).open, FileForm, unref(formState).props);
  };

  let state = reactive(getInitialState());

  const getFile = async (id) => {
    try {
      state.file = state.files?.find((file) => file._id === id);

      const response = await get(id);

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const filters = ref({
    fileType: null,
    status_cd: "active",
    po_id: null,
  });

  const { filter, pagination, params } = usePagination(filters);

  async function getFiles(options = {}) {
    try {
      options = unref(options) || {};

      if (options?.filters) {
        filters.value = options?.filters;

        delete options.filters;
      }

      options.params = { ...unref(params), ...options.params };

      const response = await index({
        ...unref(options),
      });

      state.files = response.data?.data?.data;

      pagination.value.rowsNumber = response.data?.data?.count;

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  }

  const poStore = usePurchaseOrderStore();
  const pbStore = usePurchaseBillStore();
  const invoiceStore = useInvoiceStore();
  const cnStore = useCreditNoteStore();
  const dnStore = useDebitNoteStore();
  const ewbStore = useEWayBillStore();
  const podStore = usePodStore();
  const dealSettingStore = useDealSettingStore();

  const storeMap = {
    "E-Invoice": [invoiceStore, poStore],
    "E-Way Bill": [ewbStore, poStore],
    "Purchase Order": [poStore],
    "Purchase Bill": [pbStore, poStore],
    "Proof of Delivery": [podStore, poStore],
    "Credit Note - Only Vendor's": [cnStore, poStore],
    "Debit Note - Only Customer's": [dnStore, poStore],
    "Deal Terms": [dealSettingStore],
  };

  const { getPurchaseOrderFiles } = usePurchaseOrderStore();

  const routeMappings = {
    "file-manager": getFiles,
    "sales-purchase-orders-id": getPurchaseOrderFiles,
  };

  async function postFile(
    file,
    options = {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      params: {
        showMessage: true,
      },
    },
  ) {
    try {
      const route = unref(this.router.currentRoute);

      file = prepareFile(file);

      file.routeName = route.name;

      const response = await post(file, options);

      if (routeMappings[route.name]) {
        routeMappings[route.name]();
      }

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  }

  async function postOnboardingFile(
    file,
    options = {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      params: {
        showMessage: true,
        PII: 1,
      },
    },
  ) {
    try {
      const response = await postOnboarding(file, options);

      const route = unref(this.router.currentRoute);

      if (routeMappings[route.name]) {
        routeMappings[route.name]();
      }

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  }

  async function putFile(file) {
    try {
      file = prepareFile(file);

      const response = await put(file);

      getFiles();

      storeMap?.[response.data.file_type]?.forEach((store) => {
        store.updateFile(response.data);
      });

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  }

  async function removeFile(_id, user_id) {
    try {
      const payload = {
        user_id,
      };

      const response = await remove(_id, payload);

      storeMap?.[response.data.file_type]?.forEach((store) => {
        store.updateFile(response.data);
      });

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  }

  const reset = () => {
    Object.assign(state, getInitialState());
  };

  return {
    formState,
    toggleFileForm,

    ...toRefs(state),

    filter,
    filters,
    pagination,

    getFile,
    getFiles,
    postFile,
    postOnboardingFile,
    putFile,
    removeFile,
    removeOnboardingFile,
    restoreFile,
    reset,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useFileStore, import.meta.hot));
}
